import React, { useState, useEffect, useRef } from 'react'
import { Fragment } from 'react'
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  Chip,
  Switch,
  FormControlLabel
} from '@mui/material'
import Tooltip from '@mui/material/Tooltip';
import LocationOnIcon from '@mui/icons-material/LocationOn'
import PaymentsIcon from '@mui/icons-material/Payments'
import GroupsIcon from '@mui/icons-material/Groups'
import WorkIcon from '@mui/icons-material/Work'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import CodeIcon from '@mui/icons-material/Code'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import TuneIcon from '@mui/icons-material/Tune'
import CollapseIcon from './CollapseIcon'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AIIcon from './AIIcon'
import EmptySearchStrategy from './EmptySearchStrategy'
import CandidateModal from './CandidateModal'
import {
  fetchMatches as apiFetchMatches,
  fetchProfiles,
  fetchQueries
} from '../utils/api'
import useCentralDiscoveryQueue from '../hooks/useCentralDiscoveryQueue'
import DiscoveryProgressIndicator from './DiscoveryProgressIndicator'
import centralDiscoveryQueue from '../utils/centralDiscoveryQueue'
import RefreshIcon from '@mui/icons-material/Refresh'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { updateChatSessionWithDiscoDetails } from '../utils/api'

const IntakeSummary = ({
  matchResults,
  matchCount,
  matchesLoading,
  handleRefreshResults,
  handleRefreshResultsFromQueue, // Add the queue-based refresh function
  queries,
  onSaveSearch,
  onOpenFindemSearch,
  icpRequirements,
  userInfo,
  onToggle,
  messages = [], // Add messages prop with default empty array
  sessionName = '', // Add sessionName prop with default value
  sessionId = null, // Add sessionId prop with default null
  sessionSearchId = null, // ICPID for saved Findem search (if any)
  selectedRoleTitle = null, // Add selectedRoleTitle prop with default null
  profiles = null, // Add profiles prop with default null
  onToggleCandidateView = null, // Add callback for toggling candidate view
  candidateViewMode = false, // Add prop to track current mode
  currentDiscoveryState = null, // Add currentDiscoveryState prop for progress indicator
  updateDiscoveryState = null // Add updateDiscoveryState function for progress indicator
}) => {
  // No longer need internal profiles state since it's passed as a prop
  const [modalOpen, setModalOpen] = useState(false)
  const [queriesData, setQueriesData] = useState({})

  // Use central discovery queue
  const { updateSectionType, queueItems } = useCentralDiscoveryQueue(sessionId)

  // Fetch queries data when component mounts
  useEffect(() => {
    const loadQueries = async () => {
      try {
        const queriesResponse = await fetchQueries()
        console.log(
          '🔄 INTAKE SUMMARY: Loaded queries data:',
          Object.keys(queriesResponse).length,
          'queries'
        )
        setQueriesData(queriesResponse)
      } catch (error) {
        console.error('❌ INTAKE SUMMARY: Error loading queries:', error)
      }
    }

    loadQueries()
  }, [])

  // Handle section type toggle
  const handleSectionTypeToggle = async (sectionId, currentType) => {
    const newType = currentType === 'Must Have' ? 'Nice to Have' : 'Must Have'
    console.log(
      '🔄 INTAKE SUMMARY: Toggling section type for',
      sectionId,
      'from',
      currentType,
      'to',
      newType
    )

    try {
      // 1) Persist change to central queue (local + backend)
      await updateSectionType(sectionId, newType)
      console.log('✅ INTAKE SUMMARY: Successfully updated section type')

      // 2) Build an updated view of queue items reflecting this change immediately
      const updatedQueueItems = queueItems.map(item =>
        item.section_id === sectionId
          ? { ...item, section_type: newType }
          : item
      )

      // 3) Refresh counts and matchResults using queue-driven path
      if (
        handleRefreshResultsFromQueue &&
        typeof handleRefreshResultsFromQueue === 'function'
      ) {
        console.log(
          '🔄 INTAKE SUMMARY: Refreshing counts/matchResults from updated queue items'
        )
        await handleRefreshResultsFromQueue(updatedQueueItems)
      } else if (
        handleRefreshResults &&
        typeof handleRefreshResults === 'function'
      ) {
        console.log(
          '🔄 INTAKE SUMMARY: Falling back to legacy handleRefreshResults'
        )
        await handleRefreshResults()
      }

      // 4) If candidate panel is open, refresh candidate list from updated queue as well
      try {
        if (
          typeof window !== 'undefined' &&
          typeof window.fetchMatchesFromQueue === 'function'
        ) {
          console.log(
            '🔄 INTAKE SUMMARY: Refreshing candidate list from updated queue items'
          )
          await window.fetchMatchesFromQueue(updatedQueueItems)
        }
      } catch (e) {
        console.warn('Candidate list refresh failed (non-fatal):', e)
      }
    } catch (error) {
      console.error('❌ INTAKE SUMMARY: Error updating section type:', error)
    }
  }

  // Helper function to get section type from central queue
  const getSectionTypeFromQueue = sectionId => {
    const queueItem = queueItems.find(item => item.section_id === sectionId)
    console.log(
      '🔍 INTAKE SUMMARY: getSectionTypeFromQueue for',
      sectionId,
      'found:',
      queueItem?.section_type || 'Not found'
    )
    return queueItem?.section_type || 'Must Have' // Default to Must Have if not found
  }

  // Debug: Log when queue items change
  useEffect(() => {
    console.log(
      '🔄 INTAKE SUMMARY: Queue items changed:',
      queueItems.length,
      'items'
    )
    queueItems.forEach(item => {
      console.log(
        '  - Item:',
        item.section_id,
        'type:',
        item.section_type,
        'completed:',
        item.disco_done
      )
    })
  }, [queueItems])

  // Track completed items to prevent infinite loops
  const searchInProgressRef = useRef(false)
  const lastSearchTimeRef = useRef(0)

  // Automatically trigger search when discovery items are completed
  useEffect(() => {
    const completedItems = queueItems.filter(item => item.disco_done === true)
    const now = Date.now()

    console.log('🔄 INTAKE SUMMARY: Completed items check:', {
      totalItems: queueItems.length,
      completedItems: completedItems.length,
      searchInProgress: searchInProgressRef.current,
      timeSinceLastSearch: now - lastSearchTimeRef.current,
      hasRefreshFunction: !!handleRefreshResultsFromQueue
    })

    // Only trigger search if we have completed items, not already searching, and enough time has passed
    if (
      completedItems.length > 0 &&
      handleRefreshResultsFromQueue &&
      !searchInProgressRef.current &&
      now - lastSearchTimeRef.current > 2000
    ) {
      // 2 second minimum between searches

      console.log(
        '🔄 INTAKE SUMMARY: Triggering automatic search due to completed items'
      )

      searchInProgressRef.current = true
      lastSearchTimeRef.current = now

      // Debounce the search to avoid excessive API calls
      const timeoutId = setTimeout(() => {
        handleRefreshResultsFromQueue(completedItems)
        searchInProgressRef.current = false
      }, 500)

      return () => {
        clearTimeout(timeoutId)
        searchInProgressRef.current = false
      }
    }
  }, [queueItems, handleRefreshResultsFromQueue])

  const handleOpenModal = () => {
    console.log('IntakeSummary.handleOpenModal - Called')
    console.log(
      'IntakeSummary.handleOpenModal - Current profiles prop:',
      profiles ? `exists with ${Object.keys(profiles).length} profiles` : 'null'
    )

    // Log the requirements being displayed in IntakeSummary
    const requirements =
      matchResults?.search_gen_response?.icp_requirements || []
    console.log(
      'IntakeSummary.handleOpenModal - Requirements being displayed:',
      requirements.map(req => ({
        section_name: req.section_name,
        section_id: req.section_id,
        bool_str: req.bool_str
      }))
    )

    console.log(
      'IntakeSummary.handleOpenModal - Current modalOpen state before update:',
      modalOpen
    )
    setModalOpen(true)
    console.log(
      'IntakeSummary.handleOpenModal - modalOpen state update scheduled'
    )

    // Use setTimeout to check the state after the update is applied
    setTimeout(() => {
      console.log(
        'IntakeSummary.handleOpenModal - modalOpen state after update (async check):',
        modalOpen
      )
    }, 0)
  }

  const handleCloseModal = () => {
    console.log('IntakeSummary.handleCloseModal - Called')
    setModalOpen(false)
  }

  // Handle button click - use toggle functionality
  const handleViewCandidates = () => {
    console.log('IntakeSummary.handleViewCandidates - Called')
    console.log(
      'IntakeSummary.handleViewCandidates - onToggleCandidateView:',
      typeof onToggleCandidateView
    )
    console.log(
      'IntakeSummary.handleViewCandidates - candidateViewMode:',
      candidateViewMode
    )
    console.log(
      'IntakeSummary.handleViewCandidates - profiles:',
      profiles ? `exists with ${Object.keys(profiles).length} profiles` : 'null'
    )

    if (onToggleCandidateView && typeof onToggleCandidateView === 'function') {
      console.log(
        'IntakeSummary.handleViewCandidates - Calling onToggleCandidateView'
      )
      onToggleCandidateView()
    } else {
      console.log(
        'IntakeSummary.handleViewCandidates - Falling back to handleOpenModal'
      )
      handleOpenModal()
    }
  }

  // We no longer fetch profiles when matchResults changes
  // This is now handled by MainContent.jsx to prevent infinite loops
  if (!matchResults) {
    return (
      <EmptySearchStrategy
        loading={matchesLoading}
        onToggle={onToggle}
        userInfo={userInfo}
        messages={messages}
        sessionName={sessionName}
        selectedRoleTitle={selectedRoleTitle}
      />
    )
  }

  // This function is no longer needed since profiles are passed as a prop from MainContent
  // Keeping it commented out for reference
  /*
  async function fetchMatches() {
    console.log('IntakeSummary fetchMatches called directly');
    const requirements = icpRequirements && icpRequirements.length > 0 
      ? icpRequirements 
      : (matchResults?.search_gen_response?.icp_requirements || [])
    
    console.log('Requirements for fetching matches:', requirements);
    
    try {
      console.log('Calling apiFetchMatches...');
      const data = await apiFetchMatches(requirements, { count: 9 })
      console.log('apiFetchMatches response:', data);
      
      const prids = data?.query_results?.map(result => result.profile_id) || []
      console.log('Extracted profile IDs:', prids);
      console.log('Number of profile IDs:', prids.length);
      
      if (prids.length > 0) {
        console.log('Fetching profiles for IDs...');
        const profilesData = await fetchProfiles(prids)
        console.log('fetchProfiles response:', profilesData);
        console.log('Setting profiles state with data');
        setProfiles(profilesData)
        console.log('Profiles state update scheduled');
      } else {
        console.log('No profile IDs found, profiles state will remain:', profiles);
      }
    } catch (error) {
      console.error('Error in IntakeSummary fetchMatches:', error)
    }
  }
  */

  // Helper function to parse bool_str to extract keywords or query IDs
  const parseBoolStr = boolStr => {
    if (!boolStr) return []

    // Handle JSON string format (for location)
    if (boolStr.startsWith('[{') && boolStr.endsWith('}]')) {
      try {
        const locationObj = JSON.parse(boolStr)
        if (Array.isArray(locationObj) && locationObj.length > 0) {
          // Return ALL locations, not just the first one
          return locationObj.filter(loc => loc.address).map(loc => loc.address)
        }
      } catch (e) {
        console.error('Error parsing location JSON:', e)
      }
    }

    // Remove parentheses and quotes
    let cleanedStr = boolStr.replace(/[()'"]/g, '')

    // Split by OR or AND operators
    const terms = cleanedStr.split(/\s+OR\s+|\s+AND\s+/)

    // Process each term
    const processedTerms = terms.map(term => {
      term = term.trim()

      // Check if the term is a hex ID (query ID) and resolve using queriesData
      if (/^[0-9a-f]{24}$/.test(term) && queriesData && queriesData[term]) {
        // console.log(
        //   '🔍 INTAKE SUMMARY: Resolved query ID',
        //   term,
        //   'to name:',
        //   queriesData[term].name
        // )
        return unescape(queriesData[term].name || term)
      }

      // Special case for experience ranges
      if (term.includes('workexp.overall_experience')) {
        console.log('Experience term found:', term)

        const expMatch = term.match(/workexp\.overall_experience:(\d*)-(\d+)/)
        console.log('Regex match result:', expMatch)

        if (expMatch && expMatch.length >= 3) {
          // sometimes this range reads as "-60" in which case the first match will be "" and we treat it as a 0
          const startMonth = expMatch[1] ? parseInt(expMatch[1], 10) : 0
          const endMonth = parseInt(expMatch[2])

          console.log('Parsed values:', { startMonth, endMonth })

          // Convert months to years
          const startYear = Math.floor(startMonth / 12)
          const endYear = Math.floor(endMonth / 12)

          console.log('Converted to years:', { startYear, endYear })

          // Format as a readable string
          return `${startYear}-${endYear} years`
        } else {
          console.log('Failed to match experience regex pattern')
        }
      }

      // Extract field values (e.g., workexp.current.title:Vice President -> Vice President)
      const fieldMatch = term.match(/([^:]+):(.+)/)
      if (fieldMatch && fieldMatch.length > 2) {
        // Remove quotes from the extracted value and unescape
        return unescape(fieldMatch[2].trim().replace(/^["']|["']$/g, ''))
      }

      // Remove any remaining special characters and normalize whitespace, then unescape
      return unescape(
        term
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, ' ')
          .trim()
      )
    })

    // Filter out empty strings and duplicates
    return [...new Set(processedTerms.filter(term => term && term.length > 0))]
  }

  // Extract data from search response
  const searchResponse =
    matchResults?.search_gen_response?.search_attributes || {}

  // Helper function to safely extract data
  const extractData = path => {
    return path.split('.').reduce((obj, key) => obj?.[key], searchResponse)
  }

  // Extract locations
  const extractLocations = () => {
    // First, check for user-selected locations from the discovery queue
    const completedLocationItems =
      queueItems?.filter(
        item =>
          item.topic === 'location' && item.disco_done === true && item.bool_str
      ) || []

    if (completedLocationItems.length > 0) {
      console.log(
        '🌍 INTAKE SUMMARY: Using completed location selections from queue'
      )
      const allSelectedLocations = []

      completedLocationItems.forEach(item => {
        if (item.bool_str.startsWith('[{') && item.bool_str.endsWith('}]')) {
          try {
            const locationObjects = JSON.parse(item.bool_str)
            const addresses = locationObjects
              .filter(loc => loc.address)
              .map(loc => loc.address)
            allSelectedLocations.push(...addresses)
          } catch (error) {
            console.error('Error parsing queue location bool_str:', error)
          }
        }
      })

      if (allSelectedLocations.length > 0) {
        return allSelectedLocations
      }
    }

    // Fallback to original matchResults extraction
    if (
      Array.isArray(
        matchResults?.search_gen_response?.search_attributes?.location_details
      ) &&
      matchResults.search_gen_response.search_attributes.location_details
        .length > 0
    ) {
      return matchResults.search_gen_response.search_attributes.location_details.map(
        loc => loc.formatted_address
      )
    }

    if (
      Array.isArray(extractData('included_location_details')) &&
      extractData('included_location_details').length > 0
    ) {
      return extractData('included_location_details').map(
        loc => loc.formatted_address
      )
    }

    if (
      Array.isArray(
        matchResults?.search_gen_response?.search_attributes?.included_locations
      ) &&
      matchResults.search_gen_response.search_attributes.included_locations
        .length > 0
    ) {
      return matchResults.search_gen_response.search_attributes
        .included_locations
    }

    return []
  }

  // Extract Findem Magic
  const extractFindemMagic = () => {
    // Check for findem_magic in the search response
    const findemMagic =
      matchResults?.search_gen_response?.findem_magic ||
      matchResults?.search_gen_response?.search_attributes?.findem_magic ||
      []

    // If findemMagic is an array, return it directly
    if (Array.isArray(findemMagic)) {
      return findemMagic
    }

    // If findemMagic is an object with values, extract the values
    if (findemMagic && typeof findemMagic === 'object') {
      return Object.values(findemMagic).flat().filter(Boolean)
    }

    return []
  }

  // Get data for display
  const locations = extractLocations()
  const location = locations.length > 0 ? locations.join(', ') : ''
  const findemMagic = extractFindemMagic()

  // Get the user's name from userInfo
  const userName = userInfo && userInfo.name ? userInfo.name : 'there'

  // Format TAM value - show actual numbers if less than 1000, otherwise use K format
  const tamValue =
    matchCount && matchCount.base_filter_count
      ? matchCount.base_filter_count < 1000
        ? matchCount.base_filter_count.toString()
        : `${Math.round(matchCount.base_filter_count / 1000)}K`
      : ''

  // Get the appropriate icon for a section
  const getSectionIcon = sectionId => {
    switch (sectionId) {
      case 'role':
        return <WorkIcon sx={{ fontSize: '14px' }} />
      case 'skills':
        return <CodeIcon sx={{ fontSize: '14px' }} />
      case 'workexp':
      case 'experience':
        return <LightbulbIcon sx={{ fontSize: '14px' }} />
      case 'location':
        return <LocationOnIcon sx={{ fontSize: '14px' }} />
      case 'findem_magic':
        return <AutoAwesomeIcon sx={{ fontSize: '14px' }} />
      default:
        return <WorkIcon sx={{ fontSize: '14px' }} />
    }
  }

  // Function to render dynamic sections based on completed queue items
  const renderDynamicSections = () => {
    // Use completed queue items (disco_done = true)
    const completedItems = queueItems.filter(item => item.disco_done === true)
    console.log(
      '🔄 INTAKE SUMMARY: Using completed queue items:',
      completedItems.length,
      completedItems
    )

    if (completedItems.length === 0) {
      return (
        <Typography sx={{ color: '#667085', fontStyle: 'italic' }}>
          No completed discovery items found
        </Typography>
      )
    }

    return completedItems
      .map((item, index) => {
        // Skip items without section_name or bool_str
        if (!item.section_name || !item.bool_str) {
          console.log(
            '🔄 INTAKE SUMMARY: Skipping item without section_name or bool_str:',
            item
          )
          return null
        }

        // Skip exclude_domain and sandbox sections
        if (
          item.section_id === 'exclude_domain' ||
          item.section_name.indexOf('Sandbox') >= 0
        ) {
          console.log(
            '🔄 INTAKE SUMMARY: Skipping excluded section:',
            item.section_name
          )
          return null
        }

        // Parse the bool_str to get the items
        const parsedItems = parseBoolStr(item.bool_str)
        console.log(
          '🔄 INTAKE SUMMARY: Parsed items for',
          item.section_name,
          ':',
          parsedItems
        )

        // Skip if no items
        if (parsedItems.length === 0) {
          console.log(
            '🔄 INTAKE SUMMARY: Skipping section with no parsed items:',
            item.section_name
          )
          return null
        }

        return (
          <Fragment
            key={`${item.section_id || 'unknown'}_${
              item.queue_id || index
            }_${index}`}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '1.4285714285714286em',
                    color: '#465366'
                  }}
                >
                  {item.section_name}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '12px',
                      fontWeight: 400,
                      color:
                        item.section_type === 'Must Have'
                          ? '#3B82F6'
                          : '#667085'
                    }}
                  >
                    Nice to Have
                  </Typography>
                  <Switch
                    checked={item.section_type === 'Must Have'}
                    onChange={() => {
                      handleSectionTypeToggle(
                        item.section_id,
                        item.section_type
                      )
                    }}
                    size='small'
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#3B82F6'
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        {
                          backgroundColor: '#3B82F6'
                        },
                      '& .MuiSwitch-track': {
                        backgroundColor: '#E5E7EB'
                      }
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: 'Roboto',
                      fontSize: '12px',
                      fontWeight: 400,
                      color:
                        item.section_type === 'Must Have'
                          ? '#3B82F6'
                          : '#667085'
                    }}
                  >
                    Must Have
                  </Typography>

                  {/* Restart discovery for this topic (icon) */}
                  <Tooltip title='Reset Discovery' arrow>
                    <IconButton
                      size='small'
                      aria-label='Restart discovery'
                      onClick={async () => {
                        try {
                          // 1) Reset topic items to pending in central queue (persists via backend)
                          await centralDiscoveryQueue.restartTopic(item.topic)

                          // 2) Reorder discovery sequence so this topic is first and mark as active in disco details
                          const existingOrder = Array.isArray(
                            currentDiscoveryState?.discoveryOrder
                          )
                            ? currentDiscoveryState.discoveryOrder
                            : []
                          const reordered = [
                            item.topic,
                            ...existingOrder.filter(t => t !== item.topic)
                          ]
                          const newDisco = {
                            ...currentDiscoveryState,
                            currentDiscoTopic: item.topic,
                            discoveryOrder: reordered,
                            disco_status: true // mark active
                          }

                          // Update local UI state
                          if (typeof updateDiscoveryState === 'function') {
                            updateDiscoveryState(newDisco)
                          }

                          // 3) Persist disco details on backend for this session (so queue-driven simple picks it up)
                          try {
                            if (sessionId) {
                              await updateChatSessionWithDiscoDetails(
                                sessionId,
                                { discoDetails: newDisco },
                                userInfo?.uid
                              )
                            }
                          } catch (persistErr) {
                            console.error(
                              'Persisting disco details failed:',
                              persistErr
                            )
                          }

                          // 4) Navigate to intake view to resume the topic
                          if (
                            typeof window !== 'undefined' &&
                            typeof window.goToIntakeView === 'function'
                          ) {
                            window.goToIntakeView()
                          }
                        } catch (e) {
                          console.error(
                            'Failed to restart discovery for topic:',
                            item.topic,
                            e
                          )
                        }
                      }}
                      sx={{
                        ml: 0.5,
                        color: '#667085',
                        '&:hover': {
                          backgroundColor: 'rgba(70, 83, 102, 0.06)'
                        }
                      }}
                    >
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px',
                  width: '100%'
                }}
              >
                {parsedItems.map((parsedItem, itemIndex) => {
                  const label =
                    typeof parsedItem === 'object'
                      ? parsedItem.label
                      : parsedItem

                  return (
                    <Chip
                      key={`${item.section_id}-${itemIndex}`}
                      icon={getSectionIcon(item.section_id)}
                      label={label}
                      sx={{
                        borderRadius: '16px',
                        bgcolor: '#FFFFFF',
                        border: '1px solid #EAECF0',
                        color: '#465366',
                        fontSize: '14px',
                        padding: '4px 8px',
                        '& .MuiChip-icon': {
                          color: '#465366'
                        }
                      }}
                    />
                  )
                })}
              </Box>
            </Box>

            {/* Add divider between sections except after the last one */}
            {index <
              completedItems.filter(
                i =>
                  i.section_name &&
                  i.bool_str &&
                  i.section_id !== 'exclude_domain' &&
                  i.section_name.indexOf('Sandbox') < 0 &&
                  parseBoolStr(i.bool_str).length > 0
              ).length -
                1 && <Divider sx={{ borderColor: '#EAECF0' }} />}
          </Fragment>
        )
      })
      .filter(Boolean) // Remove null items
  }

  // Create gradient background style based on Figma design
  const gradientBackground = {
    background: '#FFFFFF',
    border: '1px solid',
    borderImageSlice: 1,
    borderImageSource: `
      linear-gradient(to bottom right, 
        rgba(142, 121, 255, 0.3), 
        rgba(82, 197, 255, 0.3), 
        rgba(57, 255, 20, 0.3)
      )
    `
  }

  // Create gradient text style
  const gradientTextStyle = {
    background:
      'linear-gradient(135deg, #8E79FF 0%, #52C5FF 50%, #39FF14 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent'
  }

  // Create icon container gradient style
  const iconContainerStyle = {
    background: '#735BF3',
    border: '1px solid transparent',
    backgroundClip: 'padding-box',
    borderImage: `
      linear-gradient(135deg, rgba(115, 91, 243, 0.5), rgba(50, 225, 112, 0.5)) 1
    `
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        position: 'relative',
        ...gradientBackground,
        borderRadius: '20px'
      }}
    >
      {/* Sidebar Dragger removed - now implemented in MainContent.jsx */}

      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px',
          borderBottom: '1px solid #EAECF0',
          bgcolor: '#FFFFFF'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Box
            sx={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              bgcolor: '#735BF3',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <AIIcon size='small' color='#FFFFFF' />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '16px',
              color: '#101828'
            }}
          >
            Requirements
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '4px' }}>
          <IconButton
            size='small'
            onClick={onToggle}
            sx={{
              width: '28px',
              height: '28px',
              padding: '4px 6px',
              borderRadius: '6px',
              backgroundColor: 'transparent !important',
              border: 'none',
              '&:hover': {
                backgroundColor: 'rgba(70, 83, 102, 0.04)'
              }
            }}
          >
            <CollapseIcon size={16} color='#465366' />
          </IconButton>
        </Box>
      </Box>

      {/* Search Name + Save/Link Row */}
      {(sessionName || sessionSearchId) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            borderBottom: '1px solid #EAECF0',
            bgcolor: '#FFFFFF'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '14px',
              color: '#101828'
            }}
          >
            {sessionName || 'Current Intake'}
          </Typography>
          {sessionSearchId ? (
            <Button
              variant='text'
              size='small'
              endIcon={<OpenInNewIcon fontSize='small' />}
              onClick={() =>
                window.open(
                  `https://app-next.findem.ai/searches/${sessionSearchId}`,
                  '_blank'
                )
              }
              sx={{ textTransform: 'none' }}
            >
              Open Findem Search
            </Button>
          ) : (
            <Button
              variant='outlined'
              size='small'
              onClick={() => {
                if (typeof onSaveSearch === 'function') onSaveSearch()
              }}
              sx={{ textTransform: 'none' }}
            >
              Create Findem Search
            </Button>
          )}
        </Box>
      )}

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          padding: '32px',
          flexGrow: 1,
          overflow: 'auto' // Changed from 'hidden' to 'auto' to enable scrolling
        }}
      >
        {/* Chat Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px'
          }}
        >
          {/* Title and Location */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              flex: 1
            }}
          >
            {messages.length === 0 ? (
              // Initial state - show greeting
              <>
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 600,
                    fontSize: '20px',
                    lineHeight: '1.171875em',
                    ...gradientTextStyle
                  }}
                >
                  Hello, {userName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 600,
                    fontSize: '20px',
                    lineHeight: '1.171875em',
                    color: '#465366'
                  }}
                >
                  Start chatting to define your Search
                </Typography>
              </>
            ) : selectedRoleTitle ? (
              // Role title selected - show role title with location if available
              <>
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 600,
                    fontSize: '20px',
                    lineHeight: '1.171875em',
                    color: '#8E79FF'
                  }}
                >
                  {selectedRoleTitle}
                </Typography>
                {location && (
                  <Typography
                    sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 500,
                      fontSize: '18px',
                      lineHeight: '1.171875em',
                      color: '#465366'
                    }}
                  >
                    based in {location}
                  </Typography>
                )}
              </>
            ) : (
              // Generate a title based on available information
              <>
                {/* Try to extract role information from requirements */}
                {(() => {
                  // Look for role information in the requirements
                  const roleReq =
                    matchResults?.search_gen_response?.icp_requirements?.find(
                      req =>
                        req.section_id === 'role' ||
                        req.section_name?.toLowerCase().includes('role')
                    )

                  // Parse role information if available
                  const roleItems = roleReq
                    ? parseBoolStr(roleReq.bool_str)
                    : []
                  const roleTitle =
                    roleItems.length > 0 ? roleItems[0] : 'Software Engineer' // Default to Software Engineer

                  return (
                    <>
                      <Typography
                        sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 600,
                          fontSize: '20px',
                          lineHeight: '1.171875em',
                          color: '#8E79FF'
                        }}
                      >
                        {roleTitle}
                      </Typography>
                      {location && (
                        <Typography
                          sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 500,
                            fontSize: '18px',
                            lineHeight: '1.171875em',
                            color: '#465366'
                          }}
                        >
                          based in {location}
                        </Typography>
                      )}
                    </>
                  )
                })()}
              </>
            )}
          </Box>

          {/* Progress Indicator */}
          {currentDiscoveryState && sessionId && (
            <DiscoveryProgressIndicator
              sessionId={sessionId}
              currentDiscoveryState={currentDiscoveryState}
              updateDiscoveryState={updateDiscoveryState}
              sx={{
                alignSelf: 'flex-start',
                marginTop: '4px'
              }}
            />
          )}
        </Box>

        <Divider sx={{ borderColor: '#EAECF0' }} />

        {/* Job Details Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Roboto',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '1.4285714285714286em',
              color: '#465366'
            }}
          >
            Job Details
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}
          >
            {/* TAM Card */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                border: '1px solid #ECECEC',
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
                opacity: matchResults && tamValue ? 1 : 0.5
              }}
            >
              <Box
                sx={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  ...iconContainerStyle
                }}
              >
                <GroupsIcon sx={{ fontSize: '16px', color: '#FFFFFF' }} />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography
                  sx={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '1.375em',
                    color: '#101828'
                  }}
                >
                  Total addressable market
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '1.375em',
                  color: matchResults ? '#465366' : '#667085',
                  textAlign: 'right'
                }}
              >
                {matchResults ? tamValue : '-'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#EAECF0' }} />

        {/* Dynamically generate sections based on requirements */}
        {renderDynamicSections()}
      </Box>

      {/* Toggle View Button */}
      {matchCount && matchCount.base_filter_count > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px'
            // borderTop: '1px solid #EAECF0',
          }}
        >
          <Button
            variant='contained'
            startIcon={candidateViewMode ? <ArrowBackIcon /> : null}
            endIcon={!candidateViewMode ? <ChevronRightIcon /> : null}
            onClick={handleViewCandidates}
            sx={{
              bgcolor: '#4599FA',
              color: '#FFFFFF',
              borderRadius: '8px',
              padding: '12px',
              textTransform: 'none',
              fontFamily: 'Roboto',
              fontWeight: 400,
              width: '100%',
              fontSize: '14px',
              '&:hover': {
                bgcolor: '#3b82d6'
              }
            }}
          >
            {candidateViewMode
              ? 'Back to Intake'
              : `View ${
                  matchCount.base_filter_count < 1000
                    ? matchCount.base_filter_count
                    : `${Math.round(matchCount.base_filter_count / 1000)}K`
                } Candidates`}
          </Button>
        </Box>
      )}

      {/* Candidate Modal */}
      {profiles && (
        <CandidateModal
          open={modalOpen}
          onClose={handleCloseModal}
          candidates={profiles}
        />
      )}
    </Box>
  )
}

export default IntakeSummary
